import { fetchWithToken } from "../../../api/apiutils";

const BASE = "/api/admin/clients";

// Флаги и сортировки, которые понимает сервер. Остальные (no_bin, no_amo, inAmoCrm)
// применяются на фронте — сервер на неизвестные значения отвечает 400.
export const SERVER_FLAGS = ["expiring", "silent", "has_wins", "expired"];
export const SERVER_SORTS = [
  "name",
  "accountType",
  "clientKind",
  "niche",
  "daysLeft",
  "beta",
  "likes",
  "applications",
  "fromLikes",
  "contracts",
  "lastLogin",
  "stage",
];

export class ApiError extends Error {
  constructor(message, status, code) {
    super(message);
    this.status = status;
    this.code = code;
  }
}

// Ошибки приходят как { Message, Code, Errors }
async function readJson(res, errorText) {
  const text = await res.text().catch(() => "");
  // Вместо JSON пришла страница SPA — эндпоинта на этом сервере нет
  if (/^\s*</.test(text)) {
    throw new ApiError(
      "Сервер вернул HTML вместо JSON — эндпоинт не найден, проверьте адрес бэкенда",
      res.status,
      "NotJson",
    );
  }
  if (!res.ok) {
    let body = null;
    try {
      body = text ? JSON.parse(text) : null;
    } catch (e) {
      // не JSON
    }
    throw new ApiError(
      body?.Message || body?.message || errorText,
      res.status,
      body?.Code || body?.code || null,
    );
  }
  return text ? JSON.parse(text) : null;
}

function send(url, method, body) {
  return fetchWithToken(url, {
    method,
    body: body !== undefined ? JSON.stringify(body) : undefined,
  });
}

export function buildListParams({ q, tariff, stage, flags, includeInternal, sort, dir }) {
  const params = new URLSearchParams();
  if (q && q.trim()) params.set("q", q.trim());
  if (tariff) params.set("tariff", tariff);
  if (stage) params.set("stage", stage);
  const serverFlags = (flags || []).filter((f) => SERVER_FLAGS.includes(f));
  if (serverFlags.length) params.set("flags", serverFlags.join(","));
  if (includeInternal) params.set("includeInternal", "true");
  if (sort && SERVER_SORTS.includes(sort)) {
    params.set("sort", sort);
    if (dir) params.set("dir", dir);
  }
  return params;
}

export async function getClients(query) {
  const params = buildListParams(query);
  const res = await fetchWithToken(`${BASE}?${params.toString()}`);
  const data = await readJson(res, "Не удалось получить список клиентов");
  return {
    items: data?.items || [],
    summary: data?.summary || {},
  };
}

export async function exportClients(query) {
  const params = buildListParams(query);
  const res = await fetchWithToken(`${BASE}/export.xlsx?${params.toString()}`);
  if (!res.ok) await readJson(res, "Ошибка при выгрузке в Excel");

  let fileName = `clients_${new Date().toISOString().slice(0, 10)}.xlsx`;
  const disposition = res.headers.get("Content-Disposition");
  if (disposition) {
    const utf = disposition.match(/filename\*=UTF-8''([^;]+)/i);
    const plain = disposition.match(/filename="?([^";]+)"?/i);
    if (utf?.[1]) fileName = decodeURIComponent(utf[1]);
    else if (plain?.[1]) fileName = plain[1];
  }

  const blob = await res.blob();
  const url = window.URL.createObjectURL(blob);
  const link = document.createElement("a");
  link.href = url;
  link.setAttribute("download", fileName);
  document.body.appendChild(link);
  link.click();
  link.remove();
  window.URL.revokeObjectURL(url);
}

export async function getClient(userId) {
  const res = await fetchWithToken(`${BASE}/${userId}`);
  try {
    return await readJson(res, "Не удалось загрузить карточку клиента");
  } catch (e) {
    if (e.code === "NotAClient") e.message = "У пользователя нет подписок — это не платный клиент";
    throw e;
  }
}

export async function getManagers() {
  const res = await fetchWithToken(`${BASE}/managers`);
  return (await readJson(res, "Не удалось загрузить менеджеров")) || [];
}

export async function getClientActivity(userId) {
  const res = await fetchWithToken(`${BASE}/${userId}/activity`);
  return readJson(res, "Не удалось загрузить активность");
}

export async function getClientGoszakup(userId) {
  const res = await fetchWithToken(`${BASE}/${userId}/goszakup`);
  return readJson(res, "Не удалось загрузить данные госзакупок");
}

export async function getClientNiche(userId) {
  const res = await fetchWithToken(`${BASE}/${userId}/niche`);
  return readJson(res, "Не удалось загрузить нишу");
}

export async function getClientWork(userId) {
  const res = await fetchWithToken(`${BASE}/${userId}/work`);
  return readJson(res, "Не удалось загрузить данные по работе");
}

// 204 без тела
export async function patchClient(userId, patch) {
  const res = await send(`${BASE}/${userId}`, "PATCH", patch);
  return readJson(res, "Не удалось сохранить");
}

// 200 { bin, name, region } · 404 — нет в реестре · 400 InvalidBin
export async function lookupSupplier(bin) {
  const res = await fetchWithToken(`${BASE}/suppliers/${bin}`);
  return readJson(res, "Не удалось проверить БИН");
}

export async function addClientBin(userId, bin, confirmed) {
  const res = await send(`${BASE}/${userId}/bins`, "POST", { bin, confirmed });
  return readJson(res, "Не удалось добавить БИН");
}

export async function patchClientBin(userId, bin, confirmed) {
  const res = await send(`${BASE}/${userId}/bins/${bin}`, "PATCH", { confirmed });
  return readJson(res, "Не удалось обновить БИН");
}

export async function deleteClientBin(userId, bin) {
  const res = await send(`${BASE}/${userId}/bins/${bin}`, "DELETE");
  return readJson(res, "Не удалось удалить БИН");
}

// Считает прямо в запросе, возвращает свежий документ госзакупок или null
export async function recalcClient(userId) {
  const res = await send(`${BASE}/${userId}/recalc`, "POST");
  return readJson(res, "Не удалось пересчитать");
}

// endAt не передан → сервер выдаст на 30 дней
export async function grantFeatureAccess(userId, feature, endAt) {
  const res = await send(`${BASE}/${userId}/feature-access`, "POST", {
    feature,
    ...(endAt ? { endAt } : {}),
  });
  return readJson(res, "Не удалось выдать доступ");
}

export async function revokeFeatureAccess(userId, feature) {
  const res = await send(`${BASE}/${userId}/feature-access/${feature}`, "DELETE");
  return readJson(res, "Не удалось закрыть доступ");
}

export async function addClientTouch(userId, channel, text) {
  const res = await send(`${BASE}/${userId}/touches`, "POST", { channel, text });
  return readJson(res, "Не удалось сохранить касание");
}

// Существующие эндпоинты детализации интересов
export async function getLikedKtruStats(userId, page = 1, pageLimit = 10) {
  const res = await fetchWithToken(
    `/api/lots/favourite/byUser/ktru-statistics?UserId=${userId}&Page=${page}&PageLimit=${pageLimit}`,
  );
  return readJson(res, "Не удалось загрузить лайкнутые КТРУ");
}

export async function getSearchStats(userId, page = 1, pageLimit = 10) {
  const res = await fetchWithToken(
    `/api/filters/byUser/search-statistics?UserId=${userId}&Page=${page}&PageLimit=${pageLimit}`,
  );
  return readJson(res, "Не удалось загрузить поисковые фразы");
}

// Поиск/создание анализа компании по БИН — та же логика, что в ActiveUserTable
export async function findOrCreateBinAnalysis(bin) {
  const searchRes = await fetchWithToken(
    `/api/companies/analyses?Identifier=${bin}&Page=1&PageLimit=1`,
  );
  const searchResult = await searchRes.json();
  if (searchResult?.data?.length) return searchResult.data[0].id;

  const res = await send("/api/companies/analyses", "POST", {
    identifier: bin,
    type: "OneTime",
  });
  if (!res.ok) throw new Error(res.statusText);
  const data = await res.json();
  return data.id;
}
